import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Target, Shield, Swords, Trophy, ArrowRight, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ENSEIGNE_COLORS = {
  'Intermarché': '#10B981',
  'Système U':   '#8B5CF6',
  'Leclerc':     '#0055FF',
};

const formatCA = (v) => {
  if (!v) return '—';
  const m = v / 1_000_000;
  if (m >= 1000) return `${(m / 1000).toFixed(1)} Mds €`;
  return `${m.toFixed(0)} M€`;
};

export default function CiblesPotentielWidget() {
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/maps/targets/dashboard-stats').then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const resp = await api.get('/maps/targets/export-excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      const today = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.setAttribute('download', `cibles_gms_${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error("Erreur lors de l'export");
    } finally {
      setDownloading(false);
    }
  };

  if (!stats) return null;
  const { total, wasabi_clients, hanagroup, available, available_by_group = [] } = stats;
  const pctAvail = total > 0 ? Math.round((available / total) * 100) : 0;
  const totalCA = available_by_group.reduce((s, g) => s + (g.ca_total || 0), 0);

  return (
    <Card className="border border-[#E5E7EB] hover:shadow-md transition-shadow overflow-hidden" data-testid="cibles-potentiel-widget">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#F97316]/10 flex items-center justify-center flex-shrink-0">
              <Trophy className="w-5 h-5 text-[#F97316]" />
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Potentiel commercial</h3>
              <p className="text-[11px] text-[#71717A] mt-0.5">Cibles GMS exploitables (≥30M€ CA)</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={handleExport} disabled={downloading || available === 0}
              className="text-[11px] text-[#F97316] hover:text-[#EA580C] font-medium flex items-center gap-0.5 disabled:opacity-40"
              data-testid="cibles-widget-export-btn">
              {downloading ? <Loader2 className="w-3 h-3 animate-spin"/> : <Download className="w-3 h-3"/>}
              Excel
            </button>
            <span className="text-[#E5E7EB]">|</span>
            <button onClick={() => navigate('/carte')}
              className="text-[11px] text-[#F97316] hover:text-[#EA580C] font-medium flex items-center gap-0.5"
              data-testid="cibles-widget-open-map">
              Carte <ArrowRight className="w-3 h-3"/>
            </button>
          </div>
        </div>

        {/* Main available metric */}
        <div className="mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-[#F97316] tabular-nums" style={{ fontFamily: 'var(--font-heading)' }}>{available}</span>
            <span className="text-sm text-[#71717A]">/ {total} cibles</span>
            <span className="ml-auto text-xs bg-[#FFF7ED] text-[#C2410C] px-2 py-0.5 rounded-full font-semibold">{pctAvail}%</span>
          </div>
          <div className="mt-1 text-xs text-[#71717A]">Disponibles au démarchage</div>
          {totalCA > 0 && (
            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-[#FEF3C7] text-[#B45309] font-semibold">
              💰 CA cumulé : {formatCA(totalCA)}
            </div>
          )}
        </div>

        {/* Progress bar: available / hanagroup / wasabi */}
        <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden flex mb-3">
          <div className="bg-[#F97316]" style={{ width: `${(available / total) * 100}%` }} title={`${available} disponibles`}/>
          <div className="bg-[#FDBA74]" style={{ width: `${(hanagroup / total) * 100}%` }} title={`${hanagroup} HANAGROUP`}/>
          <div className="bg-[#10B981]" style={{ width: `${(wasabi_clients / total) * 100}%` }} title={`${wasabi_clients} déjà client`}/>
        </div>

        {/* Breakdown legend */}
        <div className="space-y-1 mb-3 text-xs">
          <div className="flex items-center gap-2">
            <Target className="w-3 h-3 text-[#F97316]"/>
            <span className="flex-1">Disponibles</span>
            <span className="font-semibold tabular-nums">{available}</span>
          </div>
          <div className="flex items-center gap-2 text-[#71717A]">
            <Swords className="w-3 h-3 text-[#EA580C]"/>
            <span className="flex-1">Positions HANAGROUP</span>
            <span className="tabular-nums">−{hanagroup}</span>
          </div>
          <div className="flex items-center gap-2 text-[#71717A]">
            <Shield className="w-3 h-3 text-[#10B981]"/>
            <span className="flex-1">Déjà clients Wasabi</span>
            <span className="tabular-nums">−{wasabi_clients}</span>
          </div>
        </div>

        {/* Breakdown by enseigne */}
        {available_by_group.length > 0 && (
          <div className="pt-3 border-t border-[#E5E7EB] space-y-1.5">
            <div className="text-[10px] uppercase tracking-wide text-[#71717A] font-semibold mb-1.5">Par enseigne</div>
            {available_by_group.map(g => (
              <div key={g.enseigne_group} className="flex items-center gap-2 text-xs" data-testid={`cibles-group-${g.enseigne_group}`}>
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ENSEIGNE_COLORS[g.enseigne_group] || '#9CA3AF' }}/>
                <span className="flex-1">{g.enseigne_group}</span>
                <span className="text-[#71717A] text-[11px]">{formatCA(g.ca_total)}</span>
                <span className="font-semibold tabular-nums min-w-[28px] text-right">{g.count}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
